// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Cluster Organisation Form
//
//  Copyright:    © 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   12.03.25 Sean Flook          GMSCM-1 Initial Revision.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import React, { useEffect, useRef, useState } from "react";

import useUserQueryStore from "../context/UserStore";
import useOrganisations from "../hooks/useOrganisations";

import ClusterErrorType from "../models/clusterErrorType";

import { Autocomplete, Box, Button, Grid2, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import ConfirmDeleteDialog, { DeleteVariant } from "../dialogs/ConfirmDeleteDialog";
import ADSErrorDisplay from "../components/ADSErrorDisplay";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { adsMidGreyA } from "../utils/ADSColour";

import { blueButtonStyle, controlLabelStyle, dataFormStyle, FormRowStyle, FormInputStyle } from "../utils/ADSStyles";

interface ClusterOrganisationFormProps {
  data: string[] | undefined;
  errors: ClusterErrorType[] | undefined;
  onDataChange: (data: string[]) => void;
  onDeleteOrganisationRecord: (name: string) => void;
  onAddOrganisationRecord: () => void;
}

const ClusterOrganisationForm: React.FC<ClusterOrganisationFormProps> = ({
  data,
  errors,
  onDataChange,
  onDeleteOrganisationRecord,
  onAddOrganisationRecord,
}) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);
  const { data: organisationLookupData, isLoading: organisationsLoading } = useOrganisations();

  const [organisations, setOrganisations] = useState<string[]>([]);
  const [organisationLookup, setOrganisationLookup] = useState<string[]>([]);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(false);
  const [organisationErrors, setOrganisationErrors] = useState<ClusterErrorType[] | undefined>(undefined);

  const deleteType = useRef<DeleteVariant | undefined>(undefined);
  const deleteRecordName = useRef<string | undefined>(undefined);

  const handleDeleteClick = (organisation: string): void => {
    deleteType.current = DeleteVariant.removeOrganisation;
    deleteRecordName.current = organisation;
    setOpenDeleteConfirmation(true);
  };

  const handleOrganisationChange = (index: number, value: string): void => {
    const updatedOrganisations = organisations.map((org, idx) => (idx === index ? value : org));
    setOrganisations(updatedOrganisations);
    if (onDataChange) onDataChange(updatedOrganisations);
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed: boolean): void => {
    setOpenDeleteConfirmation(false);

    if (deleteConfirmed) {
      if (deleteRecordName.current && onDeleteOrganisationRecord) {
        onDeleteOrganisationRecord(deleteRecordName.current);
      }
    }

    deleteRecordName.current = undefined;
    deleteType.current = undefined;
  };

  useEffect(() => {
    if (data) {
      const sortedOrganisations: string[] = [...data].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
      );
      setOrganisations(sortedOrganisations);
    } else {
      setOrganisations([]);
    }
  }, [data]);

  useEffect(() => {
    if (errors && errors.length > 0) setOrganisationErrors(errors?.filter((x) => x.field === "organisation"));
    else setOrganisationErrors(undefined);
  }, [errors]);

  useEffect(() => {
    if (!organisationsLoading) {
      const mappedOrganisations =
        organisationLookupData && organisationLookupData.length > 0
          ? [...organisationLookupData].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
          : [];
      setOrganisationLookup(mappedOrganisations);
    }
  }, [organisationLookupData, organisationsLoading]);

  return (
    <>
      <Box sx={dataFormStyle("ClusterForm")}>
        <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()} rowSpacing={2}>
          {organisationErrors && organisationErrors.length > 0 && (
            <Grid2 size={12}>
              <ADSErrorDisplay
                errorText={organisationErrors.map((x) => x.errors).join(", ")}
                id="cluster-organisation-grid-error"
              />
            </Grid2>
          )}
          <Grid2 size={12}>
            <Box sx={{ width: "100%" }}>
              <Grid2 container justifyContent="flex-start" alignItems="center" sx={FormRowStyle(false)}>
                <Grid2 size={3}>
                  <Typography variant="body2" sx={{ ...controlLabelStyle, pt: "12px" }}>
                    Organisations
                  </Typography>
                </Grid2>
                <Grid2 size={9} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                  {currentUser?.isSuperAdministrator && (
                    <Button
                      id="add-organisation-button"
                      variant="contained"
                      size="small"
                      sx={{ ...blueButtonStyle, textTransform: "none" }}
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={onAddOrganisationRecord}
                    >
                      Add organisation
                    </Button>
                  )}
                </Grid2>
              </Grid2>
              {organisations.length === 0 ? (
                <Grid2 container justifyContent="flex-start" alignItems="center" sx={FormRowStyle(false)}>
                  <Grid2 size={12}>
                    <Typography variant="body2" sx={{ fontSize: "14px", color: adsMidGreyA }}>
                      No organisations configured.
                    </Typography>
                  </Grid2>
                </Grid2>
              ) : (
                organisations.map((organisation, index) => (
                  <Grid2
                    key={`${organisation}-${index}`}
                    container
                    justifyContent="flex-start"
                    alignItems="center"
                    sx={FormRowStyle(false)}
                  >
                    <Grid2 size={12}>
                      <Autocomplete
                        id={`cluster_organisation_${index}`}
                        options={organisationLookup}
                        value={organisation}
                        disableClearable
                        fullWidth
                        disabled={!currentUser?.isSuperAdministrator}
                        onChange={(_event, newValue) => handleOrganisationChange(index, newValue ?? "")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            margin="dense"
                            sx={FormInputStyle(false)}
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {currentUser?.isSuperAdministrator && (
                                      <InputAdornment position="end">
                                        <IconButton
                                          id={`delete-organisation-${index}`}
                                          onClick={() => handleDeleteClick(organisation)}
                                          edge="end"
                                          size="small"
                                          title="Remove organisation"
                                          aria-label="Remove organisation"
                                        >
                                          <DeleteOutlineIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              },
                            }}
                          />
                        )}
                      />
                    </Grid2>
                  </Grid2>
                ))
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      {deleteType.current !== undefined && (
        <ConfirmDeleteDialog
          variant={deleteType.current}
          open={openDeleteConfirmation}
          recordName={deleteRecordName.current}
          onClose={handleCloseDeleteConfirmation}
        />
      )}
    </>
  );
};

export default ClusterOrganisationForm;
